import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Reveal } from "@/components/Reveal";
import { LevelBadge } from "@/components/LevelBadge";
import { ArrowUpRight } from "lucide-react";

export default function SupporterWall() {
  const [wall, setWall] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/supporter/wall").then((r) => setWall(r.data)).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  const platinum = wall.filter((s) => s.level === "platinum");
  const gold = wall.filter((s) => s.level === "gold");

  const Card = ({ s }) => (
    <div className="group bg-bone border border-black p-6 flex items-center gap-4 hover:border-gold transition-colors duration-300" data-testid={`wall-${s.id}`}>
      {s.avatar ? (
        <img src={s.avatar} alt={s.name} className="w-16 h-16 object-cover shrink-0" />
      ) : (
        <div className="w-16 h-16 bg-navy text-bone flex items-center justify-center font-serif text-2xl shrink-0">{s.name?.[0]}</div>
      )}
      <div className="min-w-0">
        <h3 className="font-serif text-xl tracking-tight truncate">{s.name}</h3>
        <p className="font-sans text-xs text-charcoal/50 truncate">{s.focus || s.location || "Faithful partner"}</p>
        <div className="mt-2"><LevelBadge level={s.level} size="sm" /></div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-bone pt-20 md:pt-14 pb-24">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        <Reveal>
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-black">Honor Roll</span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4">Our Supporters</h1>
          <p className="font-sans text-charcoal/60 mt-4 max-w-2xl">
            With deep gratitude, we honor the Gold & Platinum partners whose faithful generosity sustains this ministry and empowers men toward wholeness in Christ.
          </p>
        </Reveal>

        {loaded && wall.length === 0 && (
          <Reveal delay={0.1}>
            <div className="mt-16 border border-charcoal/10 p-12 text-center">
              <p className="font-serif text-2xl tracking-tight">Be the first on the wall.</p>
              <p className="font-sans text-charcoal/55 mt-3">Gold & Platinum partners who opt in are honored here.</p>
              <Link to="/supporter" data-testid="wall-cta"
                className="group inline-flex items-center gap-3 mt-8 bg-charcoal text-bone px-9 py-4 font-mono text-xs uppercase tracking-widest hover:bg-navy transition-colors">
                Become a Partner <ArrowUpRight size={16} className="hover-arrow" />
              </Link>
            </div>
          </Reveal>
        )}

        {platinum.length > 0 && (
          <div className="mt-16">
            <Reveal><div className="flex items-center gap-4"><LevelBadge level="platinum" size="md" /><span className="h-px flex-1 bg-charcoal/10" /></div></Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
              {platinum.map((s, i) => <Reveal key={s.id} delay={(i % 3) * 0.06}><Card s={s} /></Reveal>)}
            </div>
          </div>
        )}

        {gold.length > 0 && (
          <div className="mt-16">
            <Reveal><div className="flex items-center gap-4"><LevelBadge level="gold" size="md" /><span className="h-px flex-1 bg-charcoal/10" /></div></Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
              {gold.map((s, i) => <Reveal key={s.id} delay={(i % 3) * 0.06}><Card s={s} /></Reveal>)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
